import * as Sentry from "@sentry/react";
import type { Error as AppError, ErrorDebugContext } from "@/util-components/root/types";
import { recordCountMetric } from "./telemetry";

type ErrorInteractionAction = "viewed" | "report_sent";

type ErrorInteractionPayload = {
  action: ErrorInteractionAction;
  title: string;
  message: string;
  entity: string;
  statusCode: number;
  traceId?: string;
  correlationId?: string;
  debugContext?: ErrorDebugContext;
};

function summarizeDebugContext(debugContext?: ErrorDebugContext): Record<string, unknown> | undefined {
  if (!debugContext) {
    return undefined;
  }

  return {
    request: {
      method: debugContext.request.method,
      url: debugContext.request.url,
    },
    response: {
      status: debugContext.response.status,
      statusText: debugContext.response.statusText,
    },
    timestamp: debugContext.timestamp,
  };
}

/**
 * Tracks an interaction with an error message or error boundary.
 * Records a count metric and logs the interaction to Sentry.
 * 
 * @param payload - The details of the error interaction, including action, title, message, and context.
 * @returns void
 * 
 * @example
 * trackErrorInteraction({
 *   action: "viewed",
 *   title: "Network Error",
 *   message: "Failed to fetch data",
 *   entity: "USER",
 *   statusCode: 500
 * });
 */
export function trackErrorInteraction(payload: ErrorInteractionPayload): void {
  const attributes = {
    action: payload.action,
    error_entity: payload.entity || "APP_ERROR",
    status_code: payload.statusCode,
    ...(payload.traceId ? { trace_id: payload.traceId } : {}),
    ...(payload.correlationId ? { correlation_id: payload.correlationId } : {}),
  };

  recordCountMetric(`frontend.error_interaction.${payload.action}`, 1, attributes);

  Sentry.logger.error("frontend.error_interaction", {
    ...attributes,
    title: payload.title,
    error_message: payload.message,
    ...(payload.debugContext ? { debug_context: summarizeDebugContext(payload.debugContext) } : {}),
  });
}

/**
 * Tracks when a user views the detailed breakdown of an application error.
 * 
 * @param error - The application error object containing details like trace ID and status code.
 * @returns void
 */
export function trackErrorDetailsViewed(error: AppError): void {
  trackErrorInteraction({
    action: "viewed",
    title: error.title || "Unknown Error",
    message: error.message,
    entity: error.entity || "APP_ERROR",
    statusCode: error.statusCode || 0,
    traceId: error.traceId,
    correlationId: error.correlationId,
    debugContext: error.debugContext,
  });
}

/**
 * Tracks when a user manually sends an error report (e.g., via a feedback form or error dialog).
 * 
 * @param payload - The error details excluding the action type, which is automatically set to "report_sent".
 * @returns void
 */
export function trackErrorReportSent(
  payload: Omit<ErrorInteractionPayload, "action">,
): void {
  trackErrorInteraction({
    ...payload,
    action: "report_sent",
  });
}
